import React, { useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  fetchTestSessionTestlets,
  fetchTestletSessions,
  fetchTestSessionCompletionStatus,
  fetchLearnosityExamTestSessions,
  getSkillTree,
  getStandardTree,
  getLessonTree,
  getLEReport,
  getStudentSections,
} from '@apis/learnosityexams';
import { useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import { toast } from 'react-toastify';
import { useSectionStudentWithFilter } from '@reducers/sectionStudent/hooks';
import * as SessionAPIs from '@apis/sessions';
import { ISectionAvailableTest } from '@CT-Types/section';
import {
  Testlet,
  TestletSession,
  TestletSessionActivity,
  TestSessionCompletionStatus,
  ILEExamSession,
  LEReportData,
  LEStandardReportData,
  ReportTreeNode,
  StudentSections,
} from './types';
import { fillStandardScoreData, flattenStandardTree } from './utils/StandardReportHelper';
import { fillLessonScoreData, flattenLessonTree } from './utils/LessonReportHelper';
import { fillSkillScoreData, flattenSkillTree } from './utils/SkillReportHelper';

export const queryKey = {
  getLearnosityExamTestSessions: ({ sectionId }: { sectionId: number }) => [
    'LE',
    'testSessions',
    sectionId,
  ],
};

// all things related to hooks
// @note: if we place mutation hooks to mutations.ts, why don't we place the query hooks to queries.ts?

const handleError = (error: { message: string }) => {
  toast.error(`Something went wrong: ${error.message}`);
  console.error(error.message);
};

export const useGetTestlets = (
  testSessionId: number,
  options?: UseQueryOptions<Testlet[]>,
) => {
  const res = useQuery(
    ['testlets', +testSessionId],
    async () => {
      const response = (await fetchTestSessionTestlets(testSessionId)) as unknown as {
        response: {
          data: Testlet[];
        };
      };
      return response?.response?.data || ([] as Testlet[]);
    },
    {
      suspense: true,
      enabled: !!testSessionId,
      onError: handleError,
      staleTime: Infinity,
      ...options,
    },
  );
  return res;
};

export const useGetLearnosityExamTestSessions = (
  selectedSectionId?: number,
  options?: UseQueryOptions<ILEExamSession[]>,
) => {
  const { sectionId } = useParams();
  const queryClient = useQueryClient();

  const res = useQuery(
    queryKey.getLearnosityExamTestSessions({
      sectionId: selectedSectionId || +sectionId,
    }),
    async () => {
      const response = (await fetchLearnosityExamTestSessions(
        selectedSectionId || sectionId,
      )) as unknown as {
        response: {
          data: ILEExamSession[];
        };
      };
      return response?.response?.data || ([] as ILEExamSession[]);
    },
    {
      suspense: true,
      enabled: !!(selectedSectionId || sectionId),
      onError: handleError,
      staleTime: Infinity,
      refetchOnMount: 'always',
      useErrorBoundary: false,
      onSuccess: (data) => {
        data.forEach((testSession) => {
          queryClient.setQueryData(['testlets', +testSession.id], testSession.testlets);
        });
        return data;
      },
      ...options,
    },
  );
  return res;
};

export const useGetActiveTestSession = () => {
  const { testSessionId } = useParams();
  const { data: testSessions } = useGetLearnosityExamTestSessions();
  return testSessions?.find(
    (testSession) => testSession.id === parseInt(testSessionId, 10),
  );
};

export const useTestletGroupedByName = () => {
  const { data: testSessions } = useGetLearnosityExamTestSessions();
  if (!testSessions) {
    return {};
  }
  return testSessions?.reduce((acc, testSession) => {
    testSession.testlets.forEach((testlet) => {
      if (!acc[testlet.name]) {
        acc[testlet.name] = [];
      }
      acc[testlet.name].push({ ...testlet, testSessionId: testSession.id });
    });
    return acc;
  }, {} as Record<string, Array<Testlet & { testSessionId: number }>>);
};

export const useGetTestletSessions = (
  testletId: number,
  options?: UseQueryOptions<TestletSession[]>,
) => {
  const res = useQuery(
    ['testletSessions', +testletId],
    async () => {
      const response = (await fetchTestletSessions(testletId)) as unknown as {
        response: {
          data: TestletSession[];
        };
      };
      return response?.response?.data || ([] as TestletSession[]);
    },
    {
      suspense: true,
      enabled: !!testletId,
      onError: handleError,
      staleTime: Infinity,
      ...options,
    },
  );
  return res;
};

/**
 * Returns testlet sessions for a given testlet ID, filtered by the current student filter.
 * @param testletId The ID of the testlet to get sessions for
 * @returns Filtered array of testlet sessions
 */
export const useGetTestletSessionsWithFilter = (testletId: number) => {
  const { data: testletSessions } = useGetTestletSessions(testletId);
  const sectionStudents = useSectionStudentWithFilter();
  return React.useMemo(
    () =>
      testletSessions?.filter((session) =>
        sectionStudents.some((student) => student.id === session.user_id),
      ) ?? [],
    [testletSessions, sectionStudents],
  );
};

export const useNavigateToLESession = () => {
  const history = useHistory();
  const { courseId, sectionId, testSessionId: paramsTestSessionId } = useParams();
  return React.useCallback(
    (testSessionId?: number) => {
      history.push(
        `/tests/course/${courseId}/section/${sectionId}/testsession/${
          testSessionId || paramsTestSessionId
        }`,
      );
    },
    [courseId, sectionId, history, paramsTestSessionId],
  );
};

export const useNavigateToTestletSession = () => {
  const history = useHistory();
  const { courseId, sectionId, testSessionId } = useParams();

  return React.useCallback(
    (testletId: number) => {
      history.push(
        `/tests/course/${courseId}/section/${sectionId}/testsession/${testSessionId}/testlet/${testletId}/take`,
      );
    },
    [courseId, sectionId, testSessionId, history],
  );
};

export const useNavigateToLETestlet = () => {
  const history = useHistory();
  const { courseId, sectionId, testSessionId, testletId } = useParams();

  return React.useCallback(
    (
      params: {
        testSessionId?: string;
        testletId?: string;
      } = {},
    ) => {
      const session = params.testSessionId ?? testSessionId;
      const testlet = params.testletId ?? testletId;
      if (!session || !testlet) {
        return;
      }
      history.push(
        `/tests/course/${courseId}/section/${sectionId}/testsession/${
          params.testSessionId || testSessionId
        }/testlet/${params.testletId || testletId}`,
      );
    },
    [courseId, sectionId, testSessionId, testletId, history],
  );
};
export const useNavigateToLESessionReports = () => {
  const history = useHistory();
  const { courseId, sectionId, testSessionId } = useParams();

  return React.useCallback(
    (params: { testSessionId?: string } = {}) => {
      history.push(
        `/tests/course/${courseId}/section/${sectionId}/testsession/${
          params.testSessionId || testSessionId
        }/reports`,
      );
    },
    [courseId, sectionId, testSessionId, history],
  );
};
export const useNavigateToLESessionScores = () => {
  const history = useHistory();
  const { courseId, sectionId, testSessionId } = useParams();

  return React.useCallback(
    (
      params: {
        testSessionId?: string;
      } = {},
    ) => {
      history.push(
        `/tests/course/${courseId}/section/${sectionId}/testsession/${params.testSessionId}/scores`,
      );
    },
    [courseId, sectionId, testSessionId, history],
  );
};

export const useGetTestletSessionActivities = (testletSessionId: number) =>
  useQuery(
    ['testletSessionActivities', testletSessionId],
    () => [] as TestletSessionActivity[],
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

export const useGetTestSessionCompletionStatus = (courseSectionId: number) => {
  const res = useQuery(
    ['testSessionCompletionStatus', +courseSectionId],
    async () => {
      try {
        const response = (await fetchTestSessionCompletionStatus(courseSectionId)) as unknown as {
          response: {
            data: TestSessionCompletionStatus[];
          };
        };
        return response?.response?.data || ([] as TestSessionCompletionStatus[]);
      } catch (error) {
        if ((error as { response?: { status?: number } })?.response?.status === 403) {
          return [] as TestSessionCompletionStatus[];
        }
        throw error;
      }
    },
    {
      enabled: !!courseSectionId,
      staleTime: Infinity,
      retry: (failureCount, error: { response?: { status?: number } }) => error?.response?.status !== 403 && failureCount < 3,
    },
  );
  return res;
};

export const useStudentSections = (options: UseQueryOptions<StudentSections> = {}) => {
  const { testletId } = useParams();
  const res = useQuery(
    ['studentSections', +testletId],
    async () => {
      const response = (await getStudentSections(+testletId)) as unknown as {
        response: StudentSections;
      };
      return response?.response?.data || ([] as StudentSections);
    },
    {
      suspense: false,
      enabled: !!testletId,
      onError: handleError,
      staleTime: Infinity,
      ...options,
    },
  );
  return res;
};

export const useAvailableTests = (
  options: UseQueryOptions<ISectionAvailableTest[]> = {
    suspense: true,
    staleTime: Infinity,
  },
) => {
  const { courseId } = useParams();
  const data = useQuery<ISectionAvailableTest[]>({
    queryKey: ['availableTests', courseId],
    queryFn: () =>
      SessionAPIs.getCourseAvailableTests(courseId).then((res) => res.response.data),
    ...options,
  });
  return data;
};

export const useSkillTree = (
  curriculumsubject: number,
  overrideSectionId?: string | number,
  options?: UseQueryOptions<ReportTreeNode>,
) => {
  const { sectionId: paramsSectionId } = useParams();
  const sectionId = overrideSectionId ?? paramsSectionId;
  const { enabled, ...queryOptions } = options || {};
  const res = useQuery<ReportTreeNode>(
    ['skillTree', 'section', sectionId, 'curriculumsubject_id', curriculumsubject],
    async () => {
      const { response } = await getSkillTree(sectionId, {
        curriculumsubject_id: curriculumsubject,
      });
      return response?.data;
    },
    {
      suspense: true,
      staleTime: Infinity,
      enabled: enabled ?? true,
      ...queryOptions,
    },
  );
  return res;
};

export const useStandardTree = (
  curriculumsubject: number,
  overrideSectionId?: string | number,
  options?: UseQueryOptions<ReportTreeNode>,
) => {
  const { sectionId: paramsSectionId } = useParams();
  const sectionId = overrideSectionId ?? paramsSectionId;
  const { enabled, ...queryOptions } = options || {};
  const res = useQuery<ReportTreeNode>(
    ['standardTree', 'section', sectionId, 'curriculumsubject_id', curriculumsubject],
    async () => {
      const { response } = await getStandardTree(sectionId, {
        curriculumsubject_id: curriculumsubject,
      });
      return response?.data;
    },
    {
      suspense: true,
      staleTime: Infinity,
      enabled: enabled ?? true,
      ...queryOptions,
    },
  );
  return res;
};

export const useLessonTree = (
  curriculumsubject: number,
  overrideSectionId?: string | number,
  options?: UseQueryOptions<ReportTreeNode>,
) => {
  const { sectionId: paramsSectionId } = useParams();
  const sectionId = overrideSectionId ?? paramsSectionId;
  const { enabled, ...queryOptions } = options || {};
  const res = useQuery<ReportTreeNode>(
    ['lessonTree', 'section', sectionId, 'curriculumsubject_id', curriculumsubject],
    async () => {
      const { response } = await getLessonTree(sectionId, {
        curriculumsubject_id: curriculumsubject,
      });
      return response?.data;
    },
    {
      suspense: true,
      staleTime: Infinity,
      enabled: enabled ?? true,
      ...queryOptions,
    },
  );
  return res;
};

export const useLEReport = (
  reportType: 'skill' | 'standard' | 'lesson',
  examId: number,
  curriculumsubject: number[],
  overrideSectionId?: string | number,
) => {
  const { sectionId: paramsSectionId } = useParams();
  const sectionId = overrideSectionId ?? paramsSectionId;
  const res = useQuery<LEReportData>(
    [
      'LEReport',
      {
        reportType,
        examId,
        curriculumsubject,
        sectionId,
      },
    ],
    async () => {
      const response = (await getLEReport(reportType, examId, {
        section_id: sectionId,
        curriculumsubject_id: curriculumsubject,
      })) as unknown as {
        response: {
          data: LEReportData;
        };
      };
      return response?.response?.data || ({} as LEReportData);
    },
    {
      suspense: true,
      staleTime: Infinity,
    },
  );
  return res;
};

export const useLEStandardReport = (
  examId: number,
  curriculumsubject: number[],
  overrideSectionId?: string | number,
  options?: UseQueryOptions<LEStandardReportData>,
) => {
  const { sectionId: paramsSectionId } = useParams();
  const sectionId = overrideSectionId ?? paramsSectionId;
  const { enabled, ...queryOptions } = options || {};
  const { data: standardTreeData } = useStandardTree(
    curriculumsubject[0],
    overrideSectionId,
    { enabled: enabled ?? true },
  );
  const res = useQuery<LEStandardReportData>(
    ['LEStandardReport', { examId, curriculumsubject, sectionId }],
    async () => {
      const response = (await getLEReport('standard', examId, {
        section_id: sectionId,
        curriculumsubject_id: curriculumsubject,
      })) as unknown as {
        response: {
          data: LEStandardReportData;
        };
      };
      return response?.response?.data || ({} as LEStandardReportData);
    },
    {
      suspense: true,
      staleTime: Infinity,
      enabled: enabled ?? true,
      ...queryOptions,
    },
  );

  const standardScoreData = useMemo(() => {
    const rootNode = standardTreeData;
    if (!rootNode || !res.data || !rootNode.subNodes?.length) {
      return {};
    }
    const displayRoot = rootNode.subNodes[0];
    const flattenedTree = flattenStandardTree(displayRoot);
    const userIds = Object.keys(res.data);
    const standardDataObject = userIds.reduce((acc, userId) => {
      acc[userId] = JSON.parse(JSON.stringify(flattenedTree));
      return acc;
    }, {} as LEReportData);
    return fillStandardScoreData(standardDataObject, res.data, displayRoot);
  }, [standardTreeData, res.data]);
  return standardScoreData;
};

export const useLELessonReport = (
  examId: number,
  curriculumsubject: number[],
  overrideSectionId?: string | number,
  options?: UseQueryOptions<LEReportData>,
) => {
  const { sectionId: paramsSectionId } = useParams();
  const sectionId = overrideSectionId ?? paramsSectionId;
  const { enabled, ...queryOptions } = options || {};
  const { data: lessonTree } = useLessonTree(curriculumsubject[0], overrideSectionId, {
    enabled: enabled ?? true,
  });

  const lessonResults = useQuery<LEReportData>(
    ['LELessonReport', { examId, curriculumsubject, sectionId }],
    async () => {
      const response = (await getLEReport('lesson', examId, {
        section_id: sectionId,
        curriculumsubject_id: curriculumsubject,
      })) as unknown as {
        response: {
          data: LEReportData;
        };
      };
      return response?.response?.data || ({} as LEReportData);
    },
    {
      suspense: true,
      staleTime: Infinity,
      enabled: enabled ?? true,
      ...queryOptions,
    },
  );

  const lessonScoreData = useMemo(() => {
    const rootNode = lessonTree;
    if (!rootNode || !lessonResults.data || !rootNode.subNodes?.length) {
      return {};
    }
    const displayRoot = rootNode.subNodes[0];
    const flattenedTree = flattenLessonTree(displayRoot);
    const userIds = Object.keys(lessonResults.data);
    const lessonDataObject = userIds.reduce((acc, userId) => {
      acc[userId] = JSON.parse(JSON.stringify(flattenedTree));
      return acc;
    }, {} as LEReportData);
    return fillLessonScoreData(lessonDataObject, lessonResults.data, displayRoot);
  }, [lessonTree, lessonResults.data]);
  return lessonScoreData;
};

export const useLESkillReport = (
  examId: number,
  curriculumsubject: number[],
  overrideSectionId?: string | number,
  options?: UseQueryOptions<LEReportData>,
) => {
  const { sectionId: paramsSectionId } = useParams();
  const sectionId = overrideSectionId ?? paramsSectionId;
  const { enabled, ...queryOptions } = options || {};
  const { data: skillTree } = useSkillTree(curriculumsubject[0], overrideSectionId, {
    enabled: enabled ?? true,
  });

  const skillResults = useQuery<LEReportData>(
    ['LESkillReport', { examId, curriculumsubject, sectionId }],
    async () => {
      const response = (await getLEReport('skill', examId, {
        section_id: sectionId,
        curriculumsubject_id: curriculumsubject,
      })) as unknown as {
        response: {
          data: LEReportData;
        };
      };
      return response?.response?.data;
    },
    {
      suspense: true,
      staleTime: Infinity,
      enabled: enabled ?? true,
      ...queryOptions,
    },
  );

  const skillScoreData = useMemo(() => {
    const rootNode = skillTree;
    if (!rootNode || !skillResults.data || skillResults.isError || !rootNode.subNodes?.length) {
      return {};
    }
    const displayRoot = rootNode.subNodes[0];
    const flattenedTree = flattenSkillTree(displayRoot);
    const userIds = Object.keys(skillResults.data);
    if (userIds.length === 0) {
      return {};
    }
    const skillDataObject = userIds.reduce((acc, userId) => {
      acc[userId] = JSON.parse(JSON.stringify(flattenedTree));
      return acc;
    }, {} as LEReportData);
    return fillSkillScoreData(skillDataObject, skillResults.data, displayRoot);
  }, [skillTree, skillResults.data, skillResults.isError]);
  return skillScoreData;
};
